import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Droplets, Pill, Plus, RefreshCw, ShoppingBasket, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtGrams } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Chip, Loader, ModuleOff, PageHeader, Reveal, Stat } from "@/components/common";
import { DayStrip } from "@/components/DayStrip";
import { AiPlanButton, useDeviationAdapt } from "@/components/AiPlanDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MealReplaceDialog } from "@/components/MealReplaceDialog";
import { MealExtraDialog } from "@/components/MealExtraDialog";
import { todayIso, mergePlanDays } from "@/lib/dates";

const MealCard = ({ meal, done, skipped, onToggle, onSkip, onReplace, onRemove, disabled, i }) => {
  const [open, setOpen] = useState(false);
  const isExtra = !!meal.extra;
  const cardCls = done
    ? "border-food bg-food-soft"
    : skipped
      ? "border-stone-300 bg-stone-50 opacity-80"
      : isExtra
        ? "border-dashed border-food/50 bg-white"
        : "border-stone-200 bg-white";
  return (
    <Reveal delay={i * 0.05} className={`rounded-3xl border p-5 transition-colors ${cardCls}`}>
      <div className="flex items-start gap-4" data-testid={`meal-${meal.slot}`}>
        {!isExtra ? (
          <button disabled={disabled} onClick={onToggle} data-testid={`meal-toggle-${meal.slot}`} className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors active:scale-90 disabled:opacity-40 ${done ? "border-food bg-food text-white" : "border-stone-300"}`}>
            {done && <Check className="h-4 w-4" />}
          </button>
        ) : (
          <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-food bg-food text-white" aria-hidden>
            <Plus className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="eyebrow text-food-ink">{meal.label}</p>
          <p className={`mt-1 font-semibold text-ink ${skipped ? "line-through decoration-stone-400" : ""}`}>{meal.name}</p>
          {skipped && <p className="mt-1 text-xs font-semibold text-stone-500" data-testid={`meal-skipped-${meal.slot}`}>No realizada</p>}
          {meal.actual && meal.replaced_from && (
            <p className="mt-1 text-xs font-medium text-food-ink" data-testid={`meal-replaced-${meal.slot}`}>En vez de: {meal.replaced_from}</p>
          )}
          {isExtra && <p className="mt-1 text-xs font-semibold text-food-ink" data-testid={`meal-extra-badge-${meal.slot}`}>Fuera del menú planeado</p>}
          <p className="mt-1 text-sm text-stone-600">{meal.kcal} kcal · P {meal.protein} g · H {meal.carbs} g · G {meal.fat} g</p>
          {meal.notes && <p className="mt-1 text-xs italic text-stone-500">“{meal.notes}”</p>}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {!isExtra && (
              <button onClick={() => setOpen(!open)} data-testid={`meal-ingredients-toggle-${meal.slot}`} className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-food-ink">
                Ingredientes <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
            {!disabled && !isExtra && (
              <button onClick={onReplace} data-testid={`meal-replace-btn-${meal.slot}`} className="text-xs font-semibold text-food-ink hover:underline">
                {skipped || done ? "Corregir / comí otra cosa" : "Comí otra cosa"}
              </button>
            )}
            {!disabled && !isExtra && (
              <button onClick={onSkip} data-testid={`meal-skip-btn-${meal.slot}`} className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-ink">
                <X className="h-3 w-3" /> {skipped ? "Quitar omisión" : "No la hice"}
              </button>
            )}
            {!disabled && !isExtra && skipped && (
              <button onClick={onToggle} data-testid={`meal-recover-${meal.slot}`} className="text-xs font-semibold text-food-ink hover:underline">
                Sí la hice (como estaba)
              </button>
            )}
            {!disabled && isExtra && (
              <button onClick={onRemove} data-testid={`meal-extra-remove-${meal.slot}`} className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-ink">
                <X className="h-3 w-3" /> Quitar extra
              </button>
            )}
          </div>
          {open && <ul className="mt-2 space-y-1 text-sm text-stone-700">{(meal.ingredients || []).map((g) => <li key={g.name}>· {g.name} — {fmtGrams(g.grams)}</li>)}</ul>}
        </div>
      </div>
    </Reveal>
  );
};

const DayPlan = ({ plan, setPlan }) => {
  const days = useMemo(() => mergePlanDays(plan), [plan]);
  const [selDate, setSelDate] = useState(null);
  const [replaceSlot, setReplaceSlot] = useState(null);
  const [extraOpen, setExtraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const reload = () => api.get("/plan").then((r) => setPlan(r.data));
  const { trigger: triggerAdapt, dialog: adaptDialog } = useDeviationAdapt(reload);
  useEffect(() => {
    if (!days.length) return;
    const t = todayIso();
    setSelDate((prev) => {
      if (prev && days.some((d) => d.date === prev)) return prev;
      return days.some((d) => d.date === t) ? t : days[0].date;
    });
  }, [days]);
  const sel = selDate ? days.findIndex((d) => d.date === selDate) : -1;
  if (sel < 0 || !days[sel]) return <Loader />;
  const day = days[sel];
  const eaten = (day.meals || []).filter((m) => (day.meals_done || []).includes(m.slot));
  const sum = (k) => eaten.reduce((s, m) => s + m[k], 0);
  const meal = (day.meals || []).find((m) => m.slot === replaceSlot);
  const future = day.date > todayIso();
  const patchDay = (updated) => {
    const inDays = plan.days.some((d) => d.date === updated.date);
    if (inDays) setPlan({ ...plan, days: plan.days.map((d) => (d.date === updated.date ? updated : d)) });
    else setPlan({ ...plan, history: (plan.history || []).map((d) => (d.date === updated.date ? updated : d)) });
  };
  const toggle = async (slot) => {
    try {
      const { data } = await api.post(`/plan/days/${day.date}/meals/${slot}/toggle`);
      patchDay(data.day);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const skipMeal = async (slot) => {
    try {
      const { data } = await api.post(`/plan/days/${day.date}/meals/${slot}/skip`);
      const wasSkipped = (day.meals_skipped || []).includes(slot);
      patchDay(data.day);
      if (!wasSkipped) {
        triggerAdapt({ kind: "meal_skip", date: day.date, slot });
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const replace = async (body) => {
    setBusy(true);
    const slot = replaceSlot;
    try {
      const plannedName = meal?.name;
      let { data } = await api.post(`/plan/days/${day.date}/meals/${slot}/replace`, body);
      const done = new Set(data.day.meals_done || []);
      // Si estaba omitida o sin marcar, al corregir la contamos como hecha
      if (!done.has(slot)) {
        const toggled = await api.post(`/plan/days/${day.date}/meals/${slot}/toggle`);
        data = toggled.data;
      }
      patchDay(data.day);
      toast.success("Registrado lo que comiste");
      setReplaceSlot(null);
      const actualName = body.custom_name || body.meal_name || "otra cosa";
      triggerAdapt({ kind: "meal_replace", date: day.date, slot, planned: plannedName, actual: actualName });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const addExtra = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plan/days/${day.date}/meals/extra`, body);
      patchDay(data.day);
      toast.success("Extra añadido al día");
      setExtraOpen(false);
      triggerAdapt({
        kind: "meal_extra",
        date: day.date,
        actual: body.name,
        kcal: body.kcal,
        time: body.time,
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const removeExtra = async (slot) => {
    try {
      const { data } = await api.delete(`/plan/days/${day.date}/meals/${slot}`);
      patchDay(data.day);
      toast.success("Extra eliminado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DayStrip
        days={days}
        selected={sel}
        onSelect={(i) => days[i] && setSelDate(days[i].date)}
        tone="food"
        isDone={(d) => d.meals_done?.length >= 2}
        isSkipped={(d) => (d.meals_skipped || []).length > 0 && (d.meals_done || []).length < 2}
      />
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat tone="food" label="Calorías" value={`${sum("kcal")}/${day.kcal_target || "—"}`} testid="nutrition-day-kcal" />
        <Stat tone="food" label="Proteína" value={sum("protein")} unit="g" />
        <Stat tone="food" label="Hidratos" value={sum("carbs")} unit="g" />
        <Stat tone="food" label="Grasas" value={sum("fat")} unit="g" />
      </div>
      <div className="mt-5 space-y-3">
        {(day.meals || []).map((m, i) => (
          <MealCard
            key={m.slot}
            i={i}
            meal={m}
            done={(day.meals_done || []).includes(m.slot)}
            skipped={(day.meals_skipped || []).includes(m.slot)}
            onToggle={() => toggle(m.slot)}
            onSkip={() => skipMeal(m.slot)}
            onReplace={() => setReplaceSlot(m.slot)}
            onRemove={() => removeExtra(m.slot)}
            disabled={future}
          />
        ))}
        {!future && (
          <button
            type="button"
            onClick={() => setExtraOpen(true)}
            data-testid="meal-extra-open-btn"
            className="flex w-full items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-food/40 bg-white py-4 text-sm font-semibold text-food-ink transition-colors hover:bg-food-soft"
          >
            <Plus className="h-4 w-4" /> Añadir extra (cervezas, picoteo, fuera de hora…)
          </button>
        )}
      </div>
      <MealReplaceDialog
        open={!!replaceSlot}
        onOpenChange={(o) => !o && setReplaceSlot(null)}
        meal={meal}
        onSubmit={replace}
        busy={busy}
      />
      <MealExtraDialog open={extraOpen} onOpenChange={setExtraOpen} onSubmit={addExtra} busy={busy} />
      {adaptDialog}
    </>
  );
};

const ShoppingList = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(() => JSON.parse(localStorage.getItem("shopping-checked") || "[]"));
  useEffect(() => { api.get(`/nutrition/shopping-list?days=${days}`).then((r) => setData(r.data)); }, [days]);
  const toggle = (n) => {
    const next = checked.includes(n) ? checked.filter((x) => x !== n) : [...checked, n];
    setChecked(next);
    localStorage.setItem("shopping-checked", JSON.stringify(next));
  };
  return (
    <div data-testid="shopping-list">
      <div className="flex flex-wrap gap-2">{[3, 7, 14].map((n) => <Chip key={n} tone="food" active={days === n} onClick={() => setDays(n)} testid={`shopping-days-${n}`}>{n} días</Chip>)}</div>
      {!data ? <Loader /> : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {data.categories.map((c) => (
            <div key={c.category} className="card-clay p-5">
              <p className="eyebrow text-food-ink">{c.category}</p>
              <ul className="mt-3 space-y-2">
                {c.items.map((it) => (
                  <li key={it.name}>
                    <button onClick={() => toggle(it.name)} data-testid={`shopping-item-${it.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex w-full items-center gap-3 text-left text-sm">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${checked.includes(it.name) ? "border-food bg-food text-white" : "border-stone-300"}`}>{checked.includes(it.name) && <Check className="h-3 w-3" />}</span>
                      <span className={`flex-1 ${checked.includes(it.name) ? "text-stone-400 line-through" : ""}`}>{it.name}</span>
                      <span className="font-semibold text-stone-600">{fmtGrams(it.grams)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Supplements = ({ s }) => (
  <div className="space-y-3" data-testid="supplements-list">
    {!s.supplements.length && <p className="text-sm text-stone-600">No has indicado suplementos. Puedes añadirlos en tu perfil.</p>}
    {s.supplements.map((t, i) => <p key={i} className="flex gap-3 rounded-2xl bg-white p-4 text-sm"><Pill className="mt-0.5 h-4 w-4 shrink-0 text-food" />{t}</p>)}
    <p className="text-xs text-stone-500">Dosis orientativas. Consulta con un profesional antes de empezar cualquier suplemento.</p>
  </div>
);

export default function Nutrition() {
  const { me } = useAuth();
  const on = me.modules?.nutrition;
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!on) return;
    api.get("/plan").then((r) => setPlan(r.data));
    api.get("/nutrition/summary").then((r) => setSummary(r.data));
  }, [on]);
  if (!on) return <ModuleOff module="nutrition" />;
  if (!plan || !summary) return <Loader />;
  const t = summary.targets;
  const regenerate = async () => {
    try {
      const { data } = await api.post("/plan/regenerate");
      setPlan(data);
      toast.success("Plan de comidas recalculado con tu perfil actual");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="nutrition-page">
      <PageHeader eyebrow="Dietista" tone="food" title="Tu plan de comidas" subtitle="Puedes marcar o corregir también días pasados recientes.">
        <button onClick={regenerate} data-testid="nutrition-regenerate-btn" className="mt-4 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm transition-transform active:scale-95">
          <RefreshCw className="h-3.5 w-3.5" /> Recalcular plan
        </button>
        <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-food px-3 py-1.5 text-white" data-testid="nutrition-target-kcal">{t.kcal} kcal/día</span>
          <span className="rounded-full bg-white px-3 py-1.5 text-food-ink">Proteína {t.protein} g</span>
          <span className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-food-ink"><Droplets className="h-3.5 w-3.5" />{t.water_l} L agua</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <AiPlanButton testid="nutrition-ai-plan-btn" onApplied={() => api.get("/plan").then((r) => setPlan(r.data))} />
          <Link to="/agenda" data-testid="nutrition-agenda-link" className="inline-flex rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm">Ver agenda</Link>
        </div>
      </PageHeader>
      <Tabs defaultValue="plan">
        <TabsList className="mb-5 h-auto rounded-full bg-stone-100 p-1">
          <TabsTrigger value="plan" className="rounded-full px-4 py-2" data-testid="nutrition-tab-plan">Menú diario</TabsTrigger>
          <TabsTrigger value="shop" className="rounded-full px-4 py-2" data-testid="nutrition-tab-shopping"><ShoppingBasket className="mr-1.5 h-4 w-4" />Compra</TabsTrigger>
          <TabsTrigger value="supp" className="rounded-full px-4 py-2" data-testid="nutrition-tab-supplements">Suplementos</TabsTrigger>
        </TabsList>
        <TabsContent value="plan"><DayPlan plan={plan} setPlan={setPlan} /></TabsContent>
        <TabsContent value="shop"><ShoppingList /></TabsContent>
        <TabsContent value="supp"><Supplements s={summary} /></TabsContent>
      </Tabs>
    </div>
  );
}
